import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Sparkles } from "lucide-react";

const ACK_POINTS = [
  "Outputs may be incomplete or inaccurate",
  "AI-generated suggestions should not be treated as definitive conclusions",
  "All treatment decisions remain the responsibility of the practitioner",
  "The app is intended to support education, organization, and documentation only",
];

export default function Disclaimer() {
  return (
    <div className="min-h-screen bg-paper-50">
      <header className="border-b border-paper-200 bg-paper-50/80 backdrop-blur-sm sticky top-0 z-40 print:hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="disclaimer-back">
            <div className="w-9 h-9 rounded-xl bg-sage-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-lg font-medium tracking-tight text-ink-900">Posturra</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500">Massage Therapy Studio</div>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="disclaimer-page">
        <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-terracotta-500/10 text-terracotta-600 text-xs">
          <ShieldAlert className="w-3.5 h-3.5" />
          <span className="uppercase tracking-[0.14em]">Important — read in full</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900 mb-3">
          Educational & professional use disclaimer
        </h1>
        <p className="text-ink-700 mb-10 max-w-2xl">
          By using Posturra you agree to the following terms regarding the scope of this software.
        </p>

        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-10 space-y-6 text-ink-900 leading-relaxed">
          <p>
            Posturra is intended for <strong>educational, documentation, and professional support purposes only</strong>.
            This application does <strong>not diagnose medical conditions, prescribe treatment, or replace evaluation
            by a licensed medical professional</strong>.
          </p>

          <p>
            Any posture analysis, assessment suggestions, treatment priorities, protocol recommendations, or generated
            notes are <strong>informational tools</strong> designed to assist licensed massage therapists, bodyworkers,
            and wellness professionals in clinical reasoning and documentation workflows.
          </p>

          <p>
            Users are <strong>solely responsible</strong> for exercising independent professional judgment, verifying
            all information, and determining appropriate care for each client.
          </p>

          <p>
            This application is <strong>not a medical device</strong> and is not intended to provide medical advice,
            physical therapy diagnosis, chiropractic diagnosis, or healthcare treatment recommendations.
          </p>

          <p>
            Clients experiencing pain, neurological symptoms, injury, inflammation, systemic illness, or worsening
            symptoms should be referred to an appropriately licensed healthcare provider.
          </p>

          <div className="pt-2">
            <div className="label-overline mb-3">By using this application, users acknowledge that:</div>
            <ul className="space-y-2">
              {ACK_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-ink-700">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-terracotta-500 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="mt-8 text-xs text-ink-500">
          Last updated: February 2026 · Questions? Contact your account administrator.
        </p>
      </main>
    </div>
  );
}
